import React, { useEffect, useRef, useState } from 'react'
import { scrollToSelector } from '../utils/smoothScroll'
import './LandingPage.css'

function LandingPage() {
  const [menuOpen, setMenuOpen] = useState(false)
  const headerRef = useRef(null)

  // Helpers
  const getHeaderHeight = () => headerRef.current ? headerRef.current.offsetHeight : 80

  // Behaviors from script.js in React
  useEffect(() => {
    // Fade-in on scroll
    const fadeElements = Array.from(document.querySelectorAll('.fade-in'))
    const appearOptions = { threshold: 0.15, rootMargin: '0px 0px -100px 0px' }
    const appearOnScroll = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return
        entry.target.classList.add('visible')
        observer.unobserve(entry.target)
      })
    }, appearOptions)
    fadeElements.forEach(el => appearOnScroll.observe(el))

    // Smooth scrolling for anchor links using Lenis
    const onAnchorClick = (e) => {
      const anchor = e.currentTarget
      const href = anchor.getAttribute('href')
      if (!href || href === '#') return
      const target = document.querySelector(href)
      if (!target) return
      e.preventDefault()

      // Use Lenis smooth scroll with offset
      const offset = getHeaderHeight() + 10
      scrollToSelector(href, -offset)
      setMenuOpen(false)
    }
    const anchors = Array.from(document.querySelectorAll('a[href^="#"]'))
    anchors.forEach(a => a.addEventListener('click', onAnchorClick))

    // Scrollspy
    const navLinks = Array.from(document.querySelectorAll('.nav-links a'))
    const map = new Map()
    navLinks.forEach(a => { const href = a.getAttribute('href'); if (href && href.startsWith('#')) map.set(href, a) })
    const sections = Array.from(map.keys()).map(sel => document.querySelector(sel)).filter(Boolean)
    let spyObserver
    if (sections.length) {
      spyObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          const id = `#${entry.target.id}`
          const link = map.get(id)
          if (!link) return
          if (entry.isIntersecting) {
            navLinks.forEach(l => l.classList.remove('active'))
            link.classList.add('active')
          }
        })
      }, { root: null, threshold: 0.5, rootMargin: `-${getHeaderHeight()}px 0px -40% 0px` })
      sections.forEach(sec => spyObserver.observe(sec))
    }

    // Forms submit handler with Firebase integration
    const onFormSubmit = async (e) => {
      e.preventDefault()
      const form = e.currentTarget
      const status = form.querySelector('.form-status')
      const submitBtn = form.querySelector('button[type="submit"]')

      // Disable submit button
      if (submitBtn) {
        submitBtn.disabled = true
        submitBtn.dataset.prevText = submitBtn.textContent || ''
        submitBtn.textContent = 'Sending...'
        submitBtn.style.opacity = '0.7'
      }

      try {
        // Check if this is the contact form (has name, email, message fields)
        const nameInput = form.querySelector('input[name="name"]')
        const emailInput = form.querySelector('input[name="email"]')
        const messageInput = form.querySelector('textarea[name="message"]')

        if (nameInput && emailInput && messageInput) {
          // This is the contact form - send to WhatsApp
          const name = nameInput.value
          const email = emailInput.value
          const message = messageInput.value

          // Format WhatsApp message
          const whatsappMessage = `*New Contact Form Message*%0A%0A*Name:* ${encodeURIComponent(name)}%0A*Email:* ${encodeURIComponent(email)}%0A*Message:* ${encodeURIComponent(message)}`

          // WhatsApp number (without + or spaces)
          const whatsappNumber = '919890090828'

          // Open WhatsApp with pre-filled message
          const whatsappURL = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`
          window.open(whatsappURL, '_blank')

          if (status) {
            status.textContent = '✅ Redirecting to WhatsApp...'
            status.classList.add('success')
            setTimeout(() => { status.textContent = ''; status.classList.remove('success') }, 4000)
          }

          // Clear form
          form.reset()
        } else {
          // Other forms (like subscription) - just show success message
          await new Promise(r => setTimeout(r, 1000))
          if (status) {
            status.textContent = 'Success! Thank you.'
            status.classList.add('success')
            setTimeout(() => { status.textContent = ''; status.classList.remove('success') }, 4000)
          }
          form.reset()
        }
      } catch (error) {
        console.error('Error submitting form:', error)
        if (status) {
          status.textContent = '❌ Failed to send message. Please try again.'
          status.style.color = '#ef4444'
          setTimeout(() => { status.textContent = '' }, 4000)
        }
      } finally {
        // Re-enable submit button
        if (submitBtn) {
          submitBtn.disabled = false
          submitBtn.textContent = submitBtn.dataset.prevText || 'Send Message'
          submitBtn.style.opacity = '1'
          submitBtn.classList.add('sent-bounce')
          setTimeout(() => submitBtn.classList.remove('sent-bounce'), 600)
        }
      }
    }
    const forms = Array.from(document.querySelectorAll('form'))
    forms.forEach(f => f.addEventListener('submit', onFormSubmit))

    return () => {
      anchors.forEach(a => a.removeEventListener('click', onAnchorClick))
      forms.forEach(f => f.removeEventListener('submit', onFormSubmit))
      appearOnScroll.disconnect()
      if (spyObserver) spyObserver.disconnect()
    }
  }, [])

  // Testimonials data for Feedback section
  const testimonials = [
    { name: 'राहुल शर्मा', role: 'व्यापारी', location: 'मुंबई', rating: 5, comment: 'Yashasvibhav च्या सेवा खरोखरच उत्तम आहेत. आमच्या व्यवसायाला डिजिटल प्लॅटफॉर्मवर चांगली ओळख मिळाली आहे.', avatar: 'https://www.shutterstock.com/image-photo/happy-young-indian-man-smiling-260nw-334463564.jpg' },
    { name: 'प्रिया पाटील', role: 'सामाजिक कार्यकर्ता', location: 'पुणे', rating: 5, comment: 'माझ्या सामाजिक कामांसाठी उत्तम डिझाइन आणि व्हिडिओ मिळतात. त्यांची सेवा खरोखर प्रभावी आहे.', avatar: 'https://thumbs.dreamstime.com/b/portrait-beautiful-young-female-student-college-campus-41145566.jpg' },
    { name: 'अमित जोशी', role: 'डिजिटल मार्केटर', location: 'नागपूर', rating: 5, comment: 'मराठी कंटेंटसाठी हे सर्वोत्तम प्लॅटफॉर्म आहे. क्वालिटी आणि सेवा दोन्ही उत्कृष्ट आहे.', avatar: 'https://plus.unsplash.com/premium_photo-1682089787056-9ac0c78a2ac2?fm=jpg&q=60&w=3000&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NXx8aW5kaWFuJTIwcGVvcGxlfGVufDB8fDB8fHww' },
    { name: 'सुनीता देशपांडे', role: 'शिक्षिका', location: 'कोल्हापूर', rating: 4, comment: 'शैक्षणिक क्षेत्रातील काम करण्यासाठी उत्तम डिझाइन मिळतात. विद्यार्थ्यांना खूप आवडतात.', avatar: 'https://www.shutterstock.com/image-photo/cheerful-young-indian-woman-outdoors-260nw-520322230.jpg' },
    { name: 'विकास कुलकर्णी', role: 'उद्योजक', location: 'औरंगाबाद', rating: 5, comment: 'आमच्या स्टार्टअपसाठी कमी खर्चात उत्तम ब्रँडिंग सोल्यूशन मिळाले. खरोखरच संतुष्ट आहोत.', avatar: 'https://www.shutterstock.com/image-photo/portrait-laughing-man-indian-origin-260nw-604765532.jpg' },
    { name: 'मीरा वाघ', role: 'इव्हेंट ऑर्गनायझर', location: 'नाशिक', rating: 5, comment: 'सणासुदीच्या कार्यक्रमांसाठी लागणारे सर्व डिझाइन येथे मिळतात. वेळेवर आणि दर्जेदार सेवा.', avatar: 'https://media.istockphoto.com/id/519706791/photo/young-indian-woman.jpg?s=170667a&w=0&k=20&c=FpmYUoBOKladP8lQR-gU4vX0KC4aWxPMjvsa0KU2hEw=' },
  ]

  return (
    <>
      {/* Header & Navigation */}
      <header ref={headerRef}>
        <div className="container">
          <nav className="navbar">
            <div className="logo">
              <img
                src="/logo.png"
                alt="Yashasvi Bhav Logo"
                className="logo-image"
                onLoad={(e) => {
                  e.currentTarget.style.display = 'none';
                  const sib = e.currentTarget.nextElementSibling;
                  if (sib && sib.style) sib.style.display = 'inline-block';
                }}
              />
              <span className="logo-text" style={{ display: 'none' }}>Yashasvi Bhav</span>
            </div>

            <ul className={`nav-links ${menuOpen ? 'active' : ''}`}>
              <li><a href="#home">Home</a></li>
              <li><a href="#about">About</a></li>
              <li><a href="#features">Features</a></li>
              <li><a href="#videos">Video</a></li>
              <li><a href="#gallery">Gallery</a></li>
              <li><a href="#feedback">Feedback</a></li>
              <li><a href="#contact">Contact</a></li>

              {/* Mobile-only action buttons */}
              <li className="mobile-only">
                <a href="https://play.google.com/store/apps/details?id=com.spark.newyashasvibhav" className="btn btn-download mobile-nav-btn">Download App</a>
              </li>
            </ul>

            <div className="nav-actions">
              <a href="https://play.google.com/store/apps/details?id=com.spark.newyashasvibhav" className="btn btn-download">Download App</a>
            </div>

            <div className={`menu-toggle ${menuOpen ? 'active' : ''}`} onClick={() => setMenuOpen(v => !v)}>
              <i className={`fas ${menuOpen ? 'fa-times' : 'fa-bars'}`}></i>
            </div>
          </nav>
        </div>

        {/* CSS */}
        <style jsx>{`
          .navbar {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 0 20px;
          }

          .logo-image {
            width: 60px;
            height: 60px;
            border-radius: 50%;
            object-fit: cover;
            object-position: center;
            border: 3px solid #ff8800;
            transition: all 0.3s ease;
          }

          .logo-image:hover {
            transform: scale(1.1);
            box-shadow: 0 6px 20px rgba(255, 136, 0, 0.4);
          }

          .app-logo {
            width: 45px;
            height: 45px;
            border-radius: 50%;
            object-fit: cover;
            object-position: center;
          }

          .footer-logo-image {
            width: 55px;
            height: 55px;
            border-radius: 50%;
            object-fit: cover;
            object-position: center;
          }

          .nav-links {
            display: flex;
            gap: 20px;
          }

          .nav-links li a {
            transition: all 0.3s ease;
            position: relative;
          }

          .nav-links li a:hover {
            color: #fcf9f9ff;
            transform: translateY(-2px);
          }

          .nav-links li a:hover::after {
            content: '';
            position: absolute;
            bottom: -5px;
            left: 0;
            width: 100%;
            height: 2px;
            background: #f3f1f1ff;
            transform: scaleX(1);
            transition: transform 0.3s ease;
          }

          .nav-actions {
            margin-left: auto;
            display: flex;
            align-items: center;
            gap: 15px;
          }

          .nav-search {
            position: relative;
            display: flex;
            align-items: center;
           
          }

          .nav-search-input {
            width: 0;
            opacity: 0;
            transition: width 0.3s ease, opacity 0.3s ease;
            padding: 5px 10px;
            border-radius: 4px;
            border: 1px solid #ccc;
          }

          .nav-search.active .nav-search-input {
            width: 150px;
            opacity: 1;
          }

          .nav-search-btn {
            background: none;
            border: none;
            cursor: pointer;
            font-size: 1.2rem;
            margin-left: 10px;
            transition: transform 0.3s ease;
          }

          .nav-search-btn:hover {
            transform: scale(1.1);
          }

          .menu-toggle {
            margin-left: 15px;
            cursor: pointer;
            transition: transform 0.3s ease;
          }

          .menu-toggle:hover {
            transform: scale(1.1);
          }
        `}</style>
      </header>

      {/* Hero Section */}
      <section className="hero" id="home">
        <div className="hero-animated-bg" aria-hidden="true">
          <span className="orb orb-1"></span>
          <span className="orb orb-2"></span>
          <span className="orb orb-3"></span>
          <span className="orb orb-4"></span>
          <span className="orb orb-5"></span>
        </div>
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <p>तुमच्या प्रत्येक क्षणाला द्या डिजिटल स्पर्श...</p>
              <h1 className="hero-title">YASHASVI BHAVA</h1>
              <p>तुमच्या भावनांना सुंदर शब्द आणि डिझाईन्सची जोड, आता फक्त एका क्लिकवर!!</p>
              <div className="hero-cta">
                <a href="#about" className="btn btn-animated">
                  <span>Get Started Now</span>
                  <i className="fas fa-arrow-right"></i>
                </a>
                <a href="https://play.google.com/store/apps/details?id=com.spark.newyashasvibhav" className="btn btn-animated btn-outline">
                  <span>Download Now</span>
                  <i className="fas fa-download"></i>
                </a>
              </div>
            </div>
            <div className="hero-image">
              <div className="mobile-mockup">
                <div className="mobile-frame">
                  <div className="mobile-screen">
                    <div className="mobile-header">
                      <div className="status-bar">
                        <span className="time">3:37</span>
                        <div className="status-icons">
                          <i className="fas fa-signal"></i>
                          <i className="fas fa-wifi"></i>
                          <i className="fas fa-battery-three-quarters"></i>
                        </div>
                      </div>
                      <div className="app-header">
                        <i className="fas fa-arrow-left"></i>
                        <img src="/logo.png" alt="Yashasvi Bhav" className="app-logo" onError={(e) => { if (e.currentTarget && e.currentTarget.style) e.currentTarget.style.display = 'none' }} />
                        <span className="app-title">Yashasvi Bhava</span>
                        <i className="fas fa-share-alt"></i>
                      </div>
                    </div>
                    {/* Grid-based content inside the phone */}
                    <div className="mobile-content">
                      <div className="single-poster">
                        <img
                          src={`${process.env.PUBLIC_URL}/img/bg2.jpg`}
                          alt="Poster"
                          className="poster-image"
                          onLoad={(e) => {
                            // Fallback to direct path if process.env.PUBLIC_URL fails
                            if (!e.currentTarget.src.includes('/img/bg2.jpg')) {
                              e.currentTarget.src = '/img/bg2.jpg';
                              return;
                            }
                            e.currentTarget.style.background = 'linear-gradient(135deg, #ff8800, #ffd000)';
                            e.currentTarget.style.display = 'flex';
                            e.currentTarget.style.alignItems = 'center';
                            e.currentTarget.style.justifyContent = 'center';
                            e.currentTarget.style.color = 'white';
                            e.currentTarget.style.fontSize = '14px';
                            e.currentTarget.style.fontWeight = 'bold';
                            e.currentTarget.innerHTML = 'Sample Design';
                          }}
                        />
                      </div>
                      {/* Floating badges like the screenshot, but styled uniquely */}
                      {/* <div className="floating-badge premium"><i className="fas fa-star"></i> प्रीमियम</div> */}
                      <div className="floating-pill lang">मराठी <small>डिझाइन्स</small></div>

                      {/* Floating toolbox in remaining space */}
                      {/* <div className="toolbox">
                        <button className="tool-btn"><i className="fas fa-crop"></i><span>Crop</span></button>
                        <button className="tool-btn primary"><i className="fas fa-magic"></i><span>Create</span></button>
                        <button className="tool-btn"><i className="fas fa-edit"></i><span>Edit</span></button>
                      </div>*/}
                    </div>
                  </div>
                  <div className="mobile-reflection"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="about" id="about">
        <div className="container">
          <h2 className="section-title">About Yashasvi Bhav</h2>
          <div className="about-content">
            <div className="about-image fade-in">
              <div className="about-visual">
                <div className="creative-showcase">
                  <div className="showcase-item item-1">
                    <div className="mini-card birthday">
                      <i className="fas fa-birthday-cake"></i>
                      <span>Happy Birthday!</span>
                    </div>
                  </div>
                  <div className="showcase-item item-2">
                    <div className="mini-card festival">
                      <i className="fas fa-om"></i>
                      <span>Festival Wishes</span>
                    </div>
                  </div>
                  <div className="showcase-item item-3">
                    <div className="mini-card anniversary">
                      <i className="fas fa-heart"></i>
                      <span>Anniversary</span>
                    </div>
                  </div>
                  <div className="showcase-item item-4">
                    <div className="mini-card thank-you">
                      <i className="fas fa-hands-helping"></i>
                      <span>Thank You</span>
                    </div>
                  </div>
                  <div className="central-logo">
                    <div className="logo-circle">
                      <i className="fas fa-spa"></i>
                      <div className="pulse-ring"></div>
                      <div className="pulse-ring delay-1"></div>
                      <div className="pulse-ring delay-2"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="about-text fade-in">
              <div className="text-content">
                <h3><i className="fas fa-rocket"></i> Our Mission</h3>
                <p>आमच्या प्लॅटफॉर्मवर तुम्ही सर्व प्रकारचे पोस्टर्स सहज तयार करू शकता – राजकीय पोस्टर्स, विविध सणांचे संदेश, वाढदिवस व anniversary शुभेच्छा, भावपूर्ण श्रद्धांजली आणि इतर अनेक सर्जनशील डिझाईन्स. प्रत्येक पोस्टरमध्ये तुमच्या भावना आणि व्यक्तिमत्वाचा खास स्पर्श असतो, त्यामुळे ते खर्‍या अर्थाने मनाला भिडते आणि खास आठवणी निर्माण करते.</p>
                <p>आमचा प्लॅटफॉर्म वापरण्यास अगदी सोपा आहे. काही मिनिटांत तुम्ही तुमच्या आवडीप्रमाणे सुंदर आणि आकर्षक शुभेच्छा किंवा पोस्टर्स तयार करू शकता. या डिझाइन्समध्ये तुमच्या भावनांचा आणि सर्जनशीलतेचा स्पर्श असल्यामुळे ते नेहमीच लक्षात राहतात आणि प्रत्येक प्रसंगाला खास बनवतात.</p>

                <h3><i className="fas fa-star"></i> Why Choose Us</h3>
                <div className="features-list">
                  <div className="feature-point">
                    <i className="fas fa-check-circle"></i>
                    <span><strong>1000+ Templates</strong> - Handcrafted designs for every occasion</span>
                  </div>
                  <div className="feature-point">
                    <i className="fas fa-check-circle"></i>
                    <span><strong>AI-Powered Customization</strong> - Smart suggestions based on your preferences</span>
                  </div>
                  <div className="feature-point">
                    <i className="fas fa-check-circle"></i>
                    <span><strong>Instant Sharing</strong> - One-click sharing across all platforms</span>
                  </div>
                  <div className="feature-point">
                    <i className="fas fa-check-circle"></i>
                    <span><strong>Cultural Authenticity</strong> - Designs that honor traditions</span>
                  </div>
                </div>

                <div className="stats-container">
                  <div className="stat-item">
                    <div className="stat-number">50K+</div>
                    <div className="stat-label">Happy Users</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-number">1M+</div>
                    <div className="stat-label">Wishes Created</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-number">4.8★</div>
                    <div className="stat-label">App Rating</div>
                  </div>
                </div>

                <a href="#features" className="btn btn-enhanced">
                  <span>Explore Features</span>
                  <i className="fas fa-arrow-right"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features" id="features">
        <div className="container">
          <h2 className="section-title">App Features</h2>
          <div className="features-grid">
            <div className="feature-card fade-in">
              <div className="feature-icon">
                <i className="fas fa-birthday-cake"></i>
              </div>
              <h3>वाढदिवसाच्या शुभेच्छा</h3>
              <p>फोटो आणि वैयक्तिक संदेशांसह आकर्षक वाढदिवस शुभेच्छा तयार करा</p>
            </div>
            <div className="feature-card fade-in">
              <div className="feature-icon">
                <i className="fas fa-praying-hands"></i>
              </div>
              <h3>सणांच्या शुभेच्छा</h3>
              <p>नवरात्र, दिवाळी, ख्रिसमस आणि सर्व प्रमुख सणांसाठी शुभेच्छा पाठवा</p>
            </div>
            <div className="feature-card fade-in">
              <div className="feature-icon">
                <i className="fas fa-photo-video"></i>
              </div>
              <h3>फोटो आणि व्हिडिओ इफेक्ट्स</h3>
              <p>आपल्या निर्मितींना सुंदर फिल्टर्स, अ‍ॅनिमेशन आणि इफेक्ट्स जोडा</p>
            </div>
            <div className="feature-card fade-in">
              <div className="feature-icon">
                <i className="fas fa-share-alt"></i>
              </div>
              <h3>सोपे शेअरिंग</h3>
              <p>आपल्या निर्मिती व्हॉट्सॲप, फेसबुक आणि इतर प्लॅटफॉर्मवर त्वरित शेअर करा</p>
            </div>
            <div className="feature-card fade-in">
              <div className="feature-icon">
                <i className="fas fa-theater-masks"></i>
              </div>
              <h3>प्रसंगी टेम्पलेट्स</h3>
              <p>प्रत्येक प्रसंगासाठी शेकडो सुंदर टेम्पलेट्समधून निवडा</p>
            </div>
            <div className="feature-card fade-in">
              <div className="feature-icon">
                <i className="fas fa-cloud-download-alt"></i>
              </div>
              <h3>क्लाउड स्टोरेज</h3>
              <p>आपल्या निर्मिती सुरक्षितपणे जतन करा आणि कोणत्याही डिव्हाइसवरून प्रवेश करा</p>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Videos Section */}
      <section className="demo-videos" id="videos">
        <div className="container">
          <h2 className="section-title">How It Works</h2>
          <div className="how-grid">
            <div className="how-text fade-in">
              <ol className="how-steps">
                <li>
                  <span className="step-icon"><i className="fas fa-download"></i></span>
                  <div>
                    <h4>ॲप डाउनलोड करा</h4>
                    <p>Play Store/App Store वरून इंस्टॉल करा आणि साइन-इन करा.</p>
                  </div>
                </li>
                <li>
                  <span className="step-icon"><i className="fas fa-images"></i></span>
                  <div>
                    <h4>टेम्पलेट निवडा</h4>
                    <p>1000+ सण आणि कार्यक्रमांच्या डिझाइन्समधून निवडा.</p>
                  </div>
                </li>
                <li>
                  <span className="step-icon"><i className="fas fa-edit"></i></span>
                  <div>
                    <h4>सोप्या पद्धतीने कस्टमाइज करा</h4>
                    <p>तुमचा फोटो, नाव आणि संदेश काही सेकंदात जोडा.</p>
                  </div>
                </li>
                <li>
                  <span className="step-icon"><i className="fas fa-share-alt"></i></span>
                  <div>
                    <h4>शेअर करा</h4>
                    <p>WhatsApp, Instagram आणि इतर ठिकाणी पोस्ट करा.</p>
                  </div>
                </li>
              </ol>
              <a href="#download" className="btn btn-animated"><span>Get the App</span><i className="fas fa-arrow-right"></i></a>
            </div>
            <div className="how-video fade-in">
              <div className="video-container mobile-video">
                <div className="mobile-mockup video-mockup">
                  <div className="mobile-frame">
                    <div className="mobile-screen">
                      <video
                        src="/img/demo2.mp4"
                        controls
                        playsInline
                        muted
                        autoPlay
                        loop
                        poster="/img/video-poster.jpg"
                        onError={(e) => {
                          console.error('Video failed to load:', e);
                          e.target.style.background = 'linear-gradient(135deg, #ff8800, #ffd000)';
                          e.target.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100%;color:white;font-size:18px;"><i class="fas fa-play-circle" style="font-size:48px;"></i></div>';
                        }}
                      >
                        Your browser does not support the video tag.
                      </video>
                    </div>
                    <div className="mobile-reflection"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="gallery" id="gallery">
        <div className="container">
          <h2 className="section-title">Demo Gallery</h2>
          <div className="gallery-grid">
            <div className="gallery-item fade-in">
              <img src="/img/yash1.jpg" alt="Gallery 1" />
              <div className="gallery-overlay"><i className="fas fa-search-plus"></i></div>
            </div>
            <div className="gallery-item fade-in">
              <img src="/img/yash5.jpg" alt="Gallery 2" />
              <div className="gallery-overlay"><i className="fas fa-search-plus"></i></div>
            </div>
            <div className="gallery-item fade-in">
              <img src="/img/yash12.jpg" alt="Gallery 3" />
              <div className="gallery-overlay"><i className="fas fa-search-plus"></i></div>
            </div>
            <div className="gallery-item fade-in">
              <img src="/img/yash4.jpg" alt="Gallery 4" />
              <div className="gallery-overlay"><i className="fas fa-search-plus"></i></div>
            </div>
            <div className="gallery-item fade-in">
              <img src="/img/yash11.jpg" alt="Gallery 5" />
              <div className="gallery-overlay"><i className="fas fa-search-plus"></i></div>
            </div>
            <div className="gallery-item fade-in">
              <img src="/img/yash6.jpg" alt="Gallery 6" />
              <div className="gallery-overlay"><i className="fas fa-search-plus"></i></div>
            </div>
            <div className="gallery-item fade-in">
              <div className="custom-card thank-you-card">
                <i className="fas fa-gift heart"></i>
                <h3>Thank You Messages</h3>
                <p>Express gratitude with beautiful animated cards</p>
                <div className="gallery-overlay"><i className="fas fa-search-plus"></i></div>
              </div>
            </div>
            <div className="gallery-item fade-in">
              <div className="custom-card congratulations-card">
                <div className="gift-box">
                  <div className="lid"></div>
                  <div className="box"></div>
                  <div className="ribbon"></div>
                  <div className="bow"></div>
                </div>
                <h3>Congratulations</h3>
                <p>Celebrate achievements with animated gift boxes</p>
                <div className="gallery-overlay"><i className="fas fa-search-plus"></i></div>
              </div>
            </div>
          </div>
        </div>

        <style jsx>{`
    .custom-card {
      position: relative;
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 20px;
      box-sizing: border-box;
      min-height: 200px;
    }

    .thank-you-card .heart {
      font-size: 2.5rem;
      margin-bottom: 10px;
      color: #FF8800;
      animation: heartBeat 2s ease-in-out infinite;
    }

    @keyframes heartBeat {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.1); }
    }

    .congratulations-card .gift-box {
      position: relative;
      width: 60px;
      height: 60px;
      margin-bottom: 10px;
      animation: bounce 2s ease-in-out infinite;
    }

    @keyframes bounce {
      0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
      40% { transform: translateY(-10px); }
      60% { transform: translateY(-5px); }
    }

    .congratulations-card .box {
      position: absolute;
      width: 50px;
      height: 30px;
      bottom: 0;
      left: 5px;
      background: linear-gradient(45deg, #FF8800, #FFA200);
      border-radius: 4px;
    }

    .congratulations-card .lid {
      position: absolute;
      width: 60px;
      height: 15px;
      top: 15px;
      left: 0;
      background: linear-gradient(45deg, #FFB84D, #FF8800);
      border-radius: 4px 4px 0 0;
    }

    .congratulations-card .ribbon {
      position: absolute;
      width: 8px;
      height: 45px;
      top: 8px;
      left: 26px;
      background: linear-gradient(180deg, #FF6B00, #FF8800);
      border-radius: 2px;
    }

    .congratulations-card .bow {
      position: absolute;
      width: 25px;
      height: 15px;
      top: 12px;
      left: 18px;
      background: radial-gradient(circle, #FFD000, #FFA200);
      border-radius: 50% 50% 50% 50% / 60% 60% 40% 40%;
    }

    .custom-card h3 {
      font-size: 1.1rem;
      margin: 8px 0;
      color: #333;
      font-weight: 600;
    }

    .custom-card p {
      font-size: 0.85rem;
      margin: 0;
      color: #666;
      text-align: center;
    }
  `}</style>
      </section>

      {/* Subscription Section */}
      <section className="subscription" id="subscription">
        <div className="container">
          <h2 className="section-title">Stay Updated</h2>
          <p>Subscribe to our newsletter to get notified about new features, templates, and special offers</p>

          <form
            className="subscription-form fade-in"
            onSubmit={(e) => {
              e.preventDefault();
              const form = e.target;
              const input = form.querySelector("input");
              const statusDiv = form.querySelector(".form-status");

              // Show success message
              statusDiv.textContent = "✅ Subscribed successfully!";

              // Clear input
              input.value = "";

              // Hide message after 3 seconds (optional)
              setTimeout(() => {
                statusDiv.textContent = "";
              }, 3000);
            }}
          >
            <input type="email" placeholder="Your Email Address" required />
            <button type="submit">Subscribe</button>
            <div className="form-status" aria-live="polite"></div>
          </form>
        </div>
      </section>

      {/* Feedback / Testimonials Section */}
      <section id="feedback" className="feedback">
        <div className="container">
          <div className="feedback-header">
            <h2 className="section-title">Our Customers Say</h2>
            <p className="feedback-subtitle">Read honest feedback from our customers about our services</p>
          </div>
          <div className="feedback-grid">
            {testimonials.map((t, idx) => (
              <div className="feedback-card" key={idx}>
                <div className="feedback-card-head">
                  <div className="avatar"><img src={t.avatar} alt={t.name} onError={(e) => { e.currentTarget.style.visibility = 'hidden' }} /></div>
                  <div>
                    <h4 className="feedback-name">{t.name}</h4>
                    <div className="feedback-meta">{t.role} • {t.location}</div>
                  </div>
                </div>
                <div className="stars">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className={`fa-star ${i < t.rating ? 'fas' : 'far'}`}></i>
                  ))}
                </div>
                <div className="feedback-quote">
                  <i className="fas fa-quote-left quote-icon"></i>
                  <p>"{t.comment}"</p>
                </div>
              </div>
            ))}
          </div>
          <div className="feedback-stats">
            <p>आपणही आमच्या समाधानकारक ग्राहकांच्या यादीत सामील व्हा</p>
            <div className="feedback-badges">
              <span>⭐ 500+ समाधानकारक ग्राहक</span>
              <span>⭐ 4.8/5 सरासरी रेटिंग</span>
              <span>⭐ 1000+ प्रोजेक्ट पूर्ण</span>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact" id="contact">
        <div className="container">
          <h2 className="section-title">Contact Us</h2>
          <div className="contact-grid">
            <div className="contact-info fade-in">
              <h3>Get In Touch</h3>
              <div className="contact-detail">
                <div className="contact-icon">
                  <i className="fas fa-map-marker-alt"></i>
                </div>
                <div>
                  <h4>Address</h4>
                  <p>Nashik.</p>
                </div>
              </div>
              <div className="contact-detail">
                <div className="contact-icon">
                  <i className="fas fa-phone"></i>
                </div>
                <div>
                  <h4>Phone</h4>
                  <p>+91  98900 90828</p>
                </div>
              </div>
              <div className="contact-detail">
                <div className="contact-icon">
                  <i className="fas fa-envelope"></i>
                </div>
                <div>
                  <h4>Email</h4>
                  <p>yashasvibhav9@gmail.com</p>
                </div>
              </div>
              <div className="contact-detail">
                <div className="contact-icon">
                  <i className="fas fa-clock"></i>
                </div>
                <div>
                  <h4>Business Hours</h4>
                  <p>Mon-Sat: 9AM - 6PM</p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="contact-form fade-in">
              <h3>Send Message</h3>
              <form>
                <div className="form-group">
                  <label htmlFor="name">Your Name</label>
                  <input type="text" id="name" name="name" required />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Your Email</label>
                  <input type="email" id="email" name="email" required />
                </div>
                <div className="form-group">
                  <label htmlFor="message">Your Message</label>
                  <textarea id="message" name="message" required></textarea>
                </div>
                <button type="submit" className="btn">Send Message</button>
                <div className="form-status" aria-live="polite"></div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer>
        <div className="container">
          <div className="footer-content">
            <div className="footer-column">
              <div className="footer-logo">
                <img src="/logo.png" alt="Yashasvi Bhav Logo" className="footer-logo-image" onError={(e) => { e.currentTarget.style.display = 'none'; const sib = e.currentTarget.nextElementSibling; if (sib && sib.style) sib.style.display = 'inline-block' }} />
                <span className="footer-logo-text" style={{ display: 'none' }}>Yashasvi Bhav</span>
              </div>
              <p>Creating meaningful digital connections through beautiful wishes and greetings for every occasion.</p>
              <div className="social-links">
                <a href="https://www.facebook.com/share/17GNMpSCHS/" target="_blank" rel="noopener noreferrer" className="social-btn"><i className="fab fa-facebook-f"></i></a>
                <a href="https://www.instagram.com/yashasvibhavapp?igsh=MTNuZnRnaTVwNmo5dQ==" target="_blank" rel="noopener noreferrer" className="social-btn"><i className="fab fa-instagram"></i></a>
                <a href="https://twitter.com/yashasvibhav" target="_blank" rel="noopener noreferrer" className="social-btn"><i className="fab fa-twitter"></i></a>
                <a href="https://www.youtube.com/@YashasviBhavaapp" target="_blank" rel="noopener noreferrer" className="social-btn"><i className="fab fa-youtube"></i></a>
              </div>
            </div>
            <div className="footer-column">
              <h3>Quick Links</h3>
              <ul>
                <li><a href="#home">Home</a></li>
                <li><a href="#about">About</a></li>
                <li><a href="#features">Features</a></li>
                <li><a href="#videos">Video</a></li>
                <li><a href="#gallery">Gallery</a></li>
                <li><a href="#feedback">Feedback</a></li>
                <li><a href="#contact">Contact</a></li>
              </ul>
            </div>
            <div className="footer-column">
              <h3>Download</h3>
              <ul>
                <li><a href="https://play.google.com/store/apps/details?id=com.spark.newyashasvibhav" target="_blank" rel="noopener noreferrer" className="download-btn"><i className="fab fa-google-play"></i> Google Play</a></li>
                <li><a href="https://apps.apple.com/in/app/yashasvi-bhav/id1593434555" target="_blank" rel="noopener noreferrer" className="download-btn"><i className="fab fa-apple"></i> App Store</a></li>
              </ul>
            </div>
            <div className="footer-column">
              <h3>Contact Us</h3>
              <ul>
                <li><i className="fas fa-envelope"></i> yashasvibhav9@gmail.com</li>
                <li><i className="fas fa-phone"></i> +91  98900 90828</li>
                <li><i className="fas fa-map-marker-alt"></i> Nashik</li>
              </ul>
            </div>
          </div>
          <div className="copyright">
            <p> 2026 Yashasvi Bhav App. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Floating Action Buttons */}
      <div className="floating-actions">
        <a href="https://play.google.com/store/apps/details?id=com.spark.newyashasvibhav" className="fab primary" title="Download App">
          <i className="fas fa-download"></i>
        </a>
        <a href="mailto:yashasvibhav9@gmail.com" className="fab" title="yashasvibhav9@gmail.com">
          <i className="fas fa-envelope"></i>
        </a>
        <a href="tel:+9198900 90828" className="fab" title="Call Us">
          <i className="fas fa-phone"></i>
        </a>
        <a href="https://wa.me/9198900 90828" className="fab" title="WhatsApp" target="_blank" rel="noopener noreferrer">
          <i className="fab fa-whatsapp"></i>
        </a>
      </div>

      {/* Particles Background Effect */}
      <div className="particles-background">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="particle"
            style={{
              left: `${Math.random() * 100}%`,
              width: `${4 + Math.random() * 8}px`,
              height: `${4 + Math.random() * 8}px`,
              animationDelay: `${Math.random() * 15}s`
            }}
          />
        ))}
      </div>

      {/* Loading Overlay */}
      <div className="loading-overlay" id="loading">
        <div className="loading-spinner"></div>
      </div>

      {/* Add CSS for hover effects */}
      <style jsx global>{`
        /* Feature Cards Hover Effects */
        .feature-card {
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .feature-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 20px 40px rgba(0,0,0,0.1);
        }

        .feature-card:hover .feature-icon {
          transform: scale(1.1);
          color: #ff6b6b;
        }

        /* Gallery Items Hover Effects */
        .gallery-item {
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .gallery-item:hover {
          transform: scale(1.05);
          box-shadow: 0 15px 30px rgba(0,0,0,0.2);
        }

        .gallery-item:hover .gallery-overlay {
          opacity: 1;
    background: rgba(255, 136, 0, 0.7);
        }

        /* Button Hover Effects */
        .btn {
          transition: all 0.3s ease;
        }

        .btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.2);
        }

        /* Feedback Cards Hover Effects */
        .feedback-card {
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .feedback-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 30px rgba(0,0,0,0.1);
        }

        /* Subscription Form Hover Effects */
        .subscription-form input {
          transition: all 0.3s ease;
        }

        .subscription-form input:focus {
          transform: scale(1.02);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1);
        }

        .subscription-form button:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.2);
        }

        /* Contact Form Hover Effects */
        .form-group input,
        .form-group textarea {
          transition: all 0.3s ease;
        }

        .form-group input:focus,
        .form-group textarea:focus {
          transform: scale(1.02);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1);
        }

        /* Footer Links Hover Effects */
        .footer-column a {
          transition: all 0.3s ease;
        }

        .footer-column a:hover {
          color: #f5f4f4ff;
          transform: translateX(5px);
        }

        .social-links a {
          transition: all 0.3s ease;
        }

        .social-links a:hover {
          transform: translateY(-3px) scale(1.1);
        }

        /* Hero Section Button Hover Effects */
        .hero-cta .btn:hover {
          transform: translateY(-3px) scale(1.05);
        }

        /* Stats Container Hover Effects */
        .stat-item {
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .stat-item:hover {
          transform: scale(1.1);
        }

        /* Custom Cards Hover Effects */
        .custom-card {
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .custom-card:hover {
          transform: translateY(-5px) rotate(2deg);
        }

        /* Mobile Mockup Hover Effects */
        .mobile-mockup {
          transition: all 0.3s ease;
        }

        .mobile-mockup:hover {
          transform: translateY(-10px);
        }

        /* Navigation Button Base Styles */
        .btn-download {
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          font-weight: 600;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.3s ease;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 120px;
          height: 44px;
          box-sizing: border-box;
        }

        /* Download App Button Styles */
        .btn-download {
          background: linear-gradient(135deg, #ff8800, #ff6600);
          box-shadow: 0 4px 15px rgba(255, 136, 0, 0.3);
        }

        .btn-download:hover {
          background: linear-gradient(135deg, #ff6600, #e55a00);
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(255, 136, 0, 0.4);
          text-decoration: none;
          color: white;
        }

        /* Mobile Responsiveness */
        @media (max-width: 768px) {
          .btn-download {
            font-size: 0.8rem;
            padding: 8px 15px;
          }
          
          .nav-actions {
            flex-wrap: wrap;
            gap: 10px;
          }
        }
      `}</style>
    </>
  )
}

export default LandingPage;